import React, { useState, useMemo, useCallback } from 'react';

// 🔹 Wiersz w trybie desktop
const TableRow = React.memo(({ row, columns, onRowClick }) => {
  return (
    <tr
      onClick={() => onRowClick?.(row)}
      className="hover:bg-primary-50/30 transition-colors cursor-pointer"
    >
      {columns.map((col, colIdx) => {
        const cellContent = col.renderCell
          ? col.renderCell({ row, value: row[col.field] })
          : col.valueGetter
          ? col.valueGetter({ row })
          : col.valueFormatter
          ? col.valueFormatter({ value: row[col.field], row })
          : row[col.field];

        const alignRight = col.align === 'right' || col.isEnd;

        return (
          <td
            key={colIdx}
            className={`px-4 py-2 text-xs text-gray-900 whitespace-nowrap ${
              alignRight ? 'text-right' : 'text-left'
            }`}
            style={{
              width: col.width || 'auto',
              minWidth: col.width || 'auto',
            }}
          >
            {cellContent}
          </td>
        );
      })}
    </tr>
  );
});

// 🔹 Wiersz mobilny (karty)
const TableRowMobile = React.memo(({ row, columns, onRowClick }) => {
  return (
    <div
      className="p-2 bg-white rounded border border-gray-200 flex flex-col gap-2 cursor-pointer hover:bg-primary-50/30"
      onClick={() => onRowClick?.(row)}
    >
      {columns.map((col, colIdx) => {
        const cellContent = col.renderCell
          ? col.renderCell({ row, value: row[col.field] })
          : col.valueGetter
          ? col.valueGetter({ row })
          : col.valueFormatter
          ? col.valueFormatter({ value: row[col.field], row })
          : row[col.field];

        return (
          <div key={colIdx} className="flex justify-between text-xs">
            <span className="font-normal text-gray-500">{col.headerName}:</span>
            <span className="text-gray-900">{cellContent}</span>
          </div>
        );
      })}
    </div>
  );
});

export default function Table({ columns, data, loading, onRowClick }) {
  const [sortField, setSortField] = useState(null);
  const [sortDirection, setSortDirection] = useState('asc');

  // 🔹 kolumny: zwykłe na początku, te z isEnd=1 na końcu
  const preparedColumns = useMemo(() => {
    const normalCols = columns.filter((c) => !c.isEnd);
    const endCols = columns.filter((c) => c.isEnd);
    return [...normalCols, ...endCols].map((col) => ({
      ...col,
      align: col.isEnd ? 'right' : col.align,
    }));
  }, [columns]);

  const handleSort = useCallback(
    (col) => {
      if (col.sortable === false) return;
      if (sortField === col.field) {
        setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
      } else {
        setSortField(col.field);
        setSortDirection('asc');
      }
    },
    [sortField, sortDirection]
  );

  const sortedData = useMemo(() => {
    if (!sortField) return data;
    return [...data].sort((a, b) => {
      const aVal = a[sortField];
      const bVal = b[sortField];
      if (aVal === bVal) return 0;
      const comparison = aVal > bVal ? 1 : -1;
      return sortDirection === 'asc' ? comparison : -comparison;
    });
  }, [data, sortField, sortDirection]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64 sm:h-96">
        <div className="animate-spin rounded-full h-10 w-10 sm:h-12 sm:w-12 border-b-2 border-primary-700"></div>
      </div>
    );
  }

  return (
    <div className="w-full">
      {/* 🖥️ Desktop Table */}
      <div className="hidden lg:block overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200 table-fixed">
          <thead className="sticky top-0 z-10 bg-white border-b border-gray-200">
  <tr>
    {preparedColumns.map((col, idx) => {
      const alignRight = col.align === "right" || col.isEnd;
      const isSorted = sortField === col.field;

      return (
        <th
          key={`${idx}-${col.field}`}
          onClick={() => col.sortable !== false && handleSort(col)}
          className={`
            group
            px-2
            py-2
            text-xs
            font-semibold
            uppercase
            tracking-wider
            text-gray-500
            bg-gray-50
            border-b
            border-gray-200
            transition-colors
            duration-150
            whitespace-nowrap
            ${
              col.sortable !== false
                ? "cursor-pointer hover:bg-gray-100"
                : "cursor-default"
            }
            ${alignRight ? "text-right" : "text-left"}
          `}
          style={{
            width: col.width || "auto",
            minWidth: col.width || "auto",
          }}
        >
          <div
            className={`flex items-center ${
              alignRight ? "justify-end" : "justify-between"
            } gap-2`}
          >
            <span>{col.headerName}</span>

            {col.sortable !== false && (
              <span
                className={`
                  text-sm
                  transition-all
                  duration-150
                  ${
                    isSorted
                      ? "text-primary-600 opacity-100"
                      : "text-gray-300 opacity-0 group-hover:opacity-100"
                  }
                `}
              >
                {isSorted
                  ? sortDirection === "asc"
                    ? "▲"
                    : "▼"
                  : "↕"}
              </span>
            )}
          </div>
        </th>
      );
    })}
  </tr>
</thead>

          <tbody className="bg-white divide-y divide-gray-200">
            {sortedData && sortedData.length === 0 ? (
              <tr>
                <td
                  colSpan={preparedColumns.length}
                  className="px-4 py-8 text-center text-gray-500 text-sm"
                >
                  Brak danych
                </td>
              </tr>
            ) : (
              sortedData.map((row, index) => (
                <TableRow
                  key={`${row.id}-${index}`}
                  row={row}
                  columns={preparedColumns}
                  onRowClick={onRowClick}
                />
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* 📱 Mobile View */}
      <div className="lg:hidden flex flex-col gap-2">
        {sortedData &&sortedData.length === 0 ? (
          <div className="p-4 bg-white rounded border border-gray-200 text-center text-gray-500">
            Brak danych
          </div>
        ) : (
          sortedData.map((row, index) => (
            <TableRowMobile
              key={`${row.id}-${index}`}
              row={row}
              columns={preparedColumns}
              onRowClick={onRowClick}
            />
          ))
        )}
      </div>
    </div>
  );
}
